import { FC, useRef, useState } from "react";
import {
  AppCSS,
  Lbl,
  Spacer,
  TapButton,
} from "../../../../components";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";
import { SFileUpload } from "../../../../components/input/input.styled";
import { RowDiv, ColDiv } from "../../../../components/misc/misc.styled";
import { toast } from "react-toastify";
import {
  CreateProjectFileInput,
  FileType,
  ProjectFile,
} from "../../../../redux/types";
import {
  FileOpen,
  Folder,
  MoreVert,
  ArrowBack,
  InsertDriveFile,
  PictureAsPdf,
  Image as ImageIcon,
  Download,
  Edit,
  Archive,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
// import { UpdateSignedDocs } from "../../../../redux/api/estimate";
import { DOCUMENTS_TABLE_HEADER } from "../../../../contants/dashboardHeader";
import DocumentsTableHeaderCard from "../../../../components/table/CustomTableHeaderCard";
import DocumentsTableBodyCard from "../../../../components/table/CustomTableBodyCard";
import {
  useCreateProjectFile,
  useCreateUploadUrl,
  useArchiveProjectFile,
  useUpdateProjectFile,
} from "../../../../apolloClient/hooks/general/createHook";
import { CreateUploadFileInput } from "../../../../types/graphQlQuery.interface";
import { uploadImage } from "../../../../redux/api/common";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { Box, Modal, Typography, Menu, MenuItem, IconButton } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { UPDATE_DOC_VISIBILITY_QUERY } from "../../../../apolloClient/operation/querys/userQuery";
import { SelectInput } from "../../../../components/input/SelectInput";
import { SELECT_FILETYPE } from "../../../../contants/projectDetails";

interface DocumentsProps {
  onFetchProject: Function;
  project?: CustomerProjectDetails;
  docs?: ProjectFile[];
  permissions?: boolean;
}

export const DocumentDetails: FC<DocumentsProps> = ({
  onFetchProject,
  project,
  docs,
  permissions,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadUrl, setUploadUrl] = useState("");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [documentType, setDocumentType] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveFileId, setArchiveFileId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [dateOfLoss, setDateOfLoss] = useState<string>("");
  const [dateOfScope, setDateOfScope] = useState<string>("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDocId, setEditDocId] = useState<number | null>(null);
  const [editFileName, setEditFileName] = useState<string>("");
  const [editDateOfLoss, setEditDateOfLoss] = useState<string>("");
  const [editDateOfScope, setEditDateOfScope] = useState<string>("");
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  
  // State for file context menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenuDoc, setActiveMenuDoc] = useState<ProjectFile | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, doc: ProjectFile) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuDoc(doc);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMenuDoc(null);
  };
  const { createUploadUrl } = useCreateUploadUrl();
  const { createProjectFile } = useCreateProjectFile();
  const { updateProjectFile } = useUpdateProjectFile();
  const { archiveProjectFile } = useArchiveProjectFile();
  const [archiveLoading, setArchiveLoading] = useState(false);

  const [updateVisibilityQuery] = useLazyQuery(UPDATE_DOC_VISIBILITY_QUERY);

  const handleVisibilityToggle = async (doc: ProjectFile) => {
    // Default to true if undefined
    const currentVisibility = doc.customerVisibility !== false;
    const newVisibility = !currentVisibility;

    try {
      const response = await updateVisibilityQuery({
        variables: {
          docId: doc.id,
          visibility: newVisibility,
        },
      });

      if (response.data && response.data.updateDocVisibility) {
        toast("Document visibility updated successfully", {
          type: "success",
          position: "top-left",
          className: "toast-message",
        });
        onFetchProject();
      } else {
        throw new Error("Failed to update visibility");
      }
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast("Error updating document visibility", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
    }
  };

  // Helper to format date as MM/DD/YYYY
  function formatDateMMDDYYYY(dateStr: string) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  function toInputDateFormat(dateStr: string) {
    if (!dateStr) return "";
    const [mm, dd, yyyy] = dateStr.split("/");
    if (mm && dd && yyyy) return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    return "";
  }

  const getUplaodUrl = async () => {
    try {
      const file = fileRef.current?.files[0];

      const input: CreateUploadFileInput = {
        contentType: file?.type,
      };

      const success = await createUploadUrl(input);
      if (success) {
        setUploadUrl(success.url);
        setFileUrl(success.fileName);
        return;
      } else {
        throw new Error("Failed to create Upload url.");
      }
    } catch (error) {
      console.error("Error getting upload URL: ", error);
      toast("Error getting upload URL", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
    }
  };

  const handleCreateProjectFile = async () => {
    const input: CreateProjectFileInput = {
      projectId: project?.projectId,
      url: fileUrl,
      type: documentType as FileType,
      fileName: fileName.trim() || undefined, // Use fileName if provided, otherwise undefined
      ...(documentType === "INSURANCE" && {
        dateOfLoss: formatDateMMDDYYYY(dateOfLoss),
        dateOfScope: formatDateMMDDYYYY(dateOfScope),
      }),
    };

    try {
      const success = await createProjectFile(input);
      if (success) {
        setUploadUrl("");
        setDocumentType("");
        setFileUrl("");
        setFileName("");
        setDateOfLoss("");
        setDateOfScope("");
        if (fileRef.current) {
          fileRef.current.value = "";
        }
        onFetchProject();
        toast("Document uploaded successfully", {
          type: "success",
          position: "top-left",
          className: "toast-message",
        });
        
        // Track document upload
        logEvent(PROJECT_EVENTS.DOCUMENT_UPLOADED, { 
          project_id: project?.projectId, 
          document_type: documentType as FileType 
        });
        
        return;
      }
    } catch (error) {
      throw error;
    }
  };

  const onUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) {
      toast("An image have not been selected", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
      return;
    }

    try {
      const result = await uploadImage(uploadUrl, file);
      if (result.success) {
        await handleCreateProjectFile();
      }
    } catch (error) {
      console.error("Error uploading image ", error);
      toast("Error uploading document", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
    }
  };

  const onChangeFileTypeSelectInput = async (fileType: any) => {
    setDocumentType(fileType);
  };

  const handleArchiveClick = (fileId: number) => {
    setArchiveFileId(fileId);
    setArchiveModalOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!project?.projectId || !archiveFileId) return;
    setArchiveLoading(true);
    try {
      await archiveProjectFile(project.projectId, archiveFileId);
      toast("Document archived successfully", {
        type: "success",
        position: "top-left",
        className: "toast-message",
      });
      setArchiveModalOpen(false);
      setArchiveFileId(null);
      onFetchProject();
    } catch (error) {
      toast("Error archiving document", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
    }
    setArchiveLoading(false);
  };

  function formatString(str) {
    return str
      .split("_") // Split string on underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" "); // Join the words with spaces
  }

  const handleEditClick = (doc: ProjectFile) => {
    setEditDocId(doc.id);
    setEditFileName(doc.fileName || "");
    setEditDateOfLoss(toInputDateFormat(doc.dateOfLoss || ""));
    setEditDateOfScope(toInputDateFormat(doc.dateOfScope || ""));
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editDocId || !project?.projectId) return;
    try {
      await updateProjectFile({
        id: editDocId,
        projectId: project.projectId,
        fileName: editFileName.trim() || undefined,
        dateOfLoss: formatDateMMDDYYYY(editDateOfLoss),
        dateOfScope: formatDateMMDDYYYY(editDateOfScope),
      });
      toast("Document updated successfully", {
        type: "success",
        position: "top-left",
        className: "toast-message",
      });
      setEditModalOpen(false);
      setEditDocId(null);
      setEditFileName("");
      setEditDateOfLoss("");
      setEditDateOfScope("");
      onFetchProject();
    } catch (error) {
      toast("Error updating document", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
    }
  };

  const activeDocs = Array.isArray(docs)
    ? docs.filter((item) => (showArchived ? item.archived : !item.archived))
    : [];
  
  const standardTypes = SELECT_FILETYPE.map((opt) => opt.value);
  const dynamicTypes = Array.from(
    new Set(
      activeDocs
        .map((item) => item.type)
        .filter((type) => type && !standardTypes.includes(type))
    )
  );
  
  // Use a set to only keep folder types that actually have active documents
  const activeTypes = Array.from(
    new Set(activeDocs.map((item) => item.type))
  );

  const folderTypesToDisplay = activeTypes.length > 0 
    ? activeTypes 
    : standardTypes.slice(0, 4); // show some default folders if empty

  const getDocIcon = (type: string, url: string) => {
    if (url.toLowerCase().endsWith('.pdf')) return <PictureAsPdf sx={{ color: '#ef4444' }} />;
    return <InsertDriveFile sx={{ color: '#6b7280' }} />;
  };

  const getFileDisplayName = (file: ProjectFile) => {
    if (file.fileName) return formatString(file.fileName);
    if (file.createdAt) {
      let d = new Date(file.createdAt);
      if (isNaN(d.getTime())) {
        d = new Date(Number(file.createdAt));
      }
      if (!isNaN(d.getTime())) return `${formatString(file.type)} - ${d.toLocaleDateString()}`;
    }
    return formatString(file.type);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Box
          onClick={() => setShowArchived((prev) => !prev)}
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1,
            backgroundColor: showArchived ? "#10b981" : "#f59e42",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: { xs: 12, sm: 13 },
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {showArchived ? "Show Active Docs" : "Show Archived Docs"}
        </Box>
        {permissions && (
          <Box
            onClick={() => setModalOpen(true)}
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1,
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: { xs: 12, sm: 14 },
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
              "&:hover": { backgroundColor: "#2563eb" },
            }}
          >
            + Upload Document
          </Box>
        )}
      </Box>

      {/* Document Grid View */}
      <Box sx={{ mt: 3, mb: 4 }}>
        {!selectedFolder ? (
          /* Folder View (Root) */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
              gap: 3,
            }}
          >
            {folderTypesToDisplay.map((type, index) => {
              const fileCount = activeDocs.filter(d => d.type === type).length;
              return (
                <Box
                  key={`folder-${index}`}
                  onClick={() => setSelectedFolder(type)}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    p: 3,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#d1d5db",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      transform: "translateY(-2px)"
                    }
                  }}
                >
                  <Folder sx={{ color: "#10b981", fontSize: 44 }} />
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#1f2937", mb: 0.5 }}>
                      {formatString(type)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                      {fileCount} {fileCount === 1 ? "file" : "files"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          /* File View (Inside a Folder) */
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
              <IconButton onClick={() => setSelectedFolder(null)} sx={{ color: "#4b5563" }}>
                <ArrowBack />
              </IconButton>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#1f2937" }}>
                {formatString(selectedFolder)}
              </Typography>
            </Box>
            
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                gap: 3,
              }}
            >
              {activeDocs
                .filter(d => d.type === selectedFolder)
                .map((file, idx) => {
                  const isImage = file.url.toLowerCase().match(/\.(jpeg|jpg|gif|png)$/);
                  return (
                  <Box
                    key={`file-${idx}`}
                    sx={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      p: isImage ? 1 : 3,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "#d1d5db",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
                      }
                    }}
                  >
                    {file.customerVisibility === false && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -10,
                          left: -10,
                          backgroundColor: "#f3f4f6",
                          border: "1px solid #d1d5db",
                          borderRadius: "20px",
                          px: 1.5,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          zIndex: 10,
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}
                      >
                        <VisibilityOff sx={{ fontSize: 14, color: "#4b5563" }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#4b5563" }}>Hidden</Typography>
                      </Box>
                    )}
                    {isImage ? (
                      <Box sx={{ width: "100%", height: 160, borderRadius: "8px", overflow: "hidden", position: "relative" }}>
                        <img src={file.url} alt="thumbnail" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, file)}
                          sx={{ 
                            position: "absolute", 
                            top: 8, 
                            right: 8, 
                            backgroundColor: "rgba(255,255,255,0.8)", 
                            "&:hover": { backgroundColor: "rgba(255,255,255,1)" } 
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          {getDocIcon(file.type, file.url)}
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, file)}
                            sx={{ color: "#9ca3af", p: 0.5, mr: -1, mt: -1 }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: file.fileName ? 600 : 400,
                              fontStyle: file.fileName ? "normal" : "italic",
                              color: file.fileName ? "#1f2937" : "#6b7280",
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical"
                            }}
                          >
                            {getFileDisplayName(file)}
                          </Typography>
                          {/* {file.type === "INSURANCE" && (
                            <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                              Loss: {file.dateOfLoss || "N/A"}
                            </Typography>
                          )} */}
                        </Box>
                      </>
                    )}
                  </Box>
                )})}
            </Box>
          </Box>
        )}
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 160, borderRadius: 2, mt: 1, border: "1px solid #f3f4f6" }
        }}
      >
        <MenuItem
          onClick={() => {
            if (activeMenuDoc?.url) window.open(activeMenuDoc.url, "_blank");
            handleMenuClose();
          }}
          sx={{ py: 1.5, fontSize: 14, color: "#374151" }}
        >
          <Visibility sx={{ mr: 2, fontSize: 20, color: "#6b7280" }} /> 
          {((activeMenuDoc?.type === "ESTIMATE" || activeMenuDoc?.type === "AFFIDAVIT") && activeMenuDoc?.unsignedUrl) ? "View Signed" : "View"}
        </MenuItem>

        {(activeMenuDoc?.type === "ESTIMATE" || activeMenuDoc?.type === "AFFIDAVIT") && activeMenuDoc?.unsignedUrl && (
          <MenuItem
            onClick={() => {
              if (activeMenuDoc?.unsignedUrl) window.open(activeMenuDoc.unsignedUrl, "_blank");
              handleMenuClose();
            }}
            sx={{ py: 1.5, fontSize: 14, color: "#374151" }}
          >
            <Visibility sx={{ mr: 2, fontSize: 20, color: "#6b7280" }} /> View Unsigned
          </MenuItem>
        )}

        {permissions && activeMenuDoc && (
          <MenuItem
            onClick={() => {
              if (activeMenuDoc) handleVisibilityToggle(activeMenuDoc);
              handleMenuClose();
            }}
            sx={{ py: 1.5, fontSize: 14, color: "#374151" }}
          >
            {activeMenuDoc.customerVisibility === false ? (
              <>
                <Visibility sx={{ mr: 2, fontSize: 20, color: "#6b7280" }} /> Show to Customer
              </>
            ) : (
              <>
                <VisibilityOff sx={{ mr: 2, fontSize: 20, color: "#6b7280" }} /> Hide from Customer
              </>
            )}
          </MenuItem>
        )}

        {permissions && !showArchived && activeMenuDoc?.type === "INSURANCE" && (
          <MenuItem
            onClick={() => {
              if (activeMenuDoc) handleEditClick(activeMenuDoc);
              handleMenuClose();
            }}
            sx={{ py: 1.5, fontSize: 14, color: "#374151" }}
          >
            <Edit sx={{ mr: 2, fontSize: 20, color: "#6b7280" }} /> Edit
          </MenuItem>
        )}

        {permissions && !showArchived && (
          <MenuItem
            onClick={() => {
              if (activeMenuDoc) handleArchiveClick(activeMenuDoc.id);
              handleMenuClose();
            }}
            sx={{ py: 1.5, fontSize: 14, color: "#ef4444" }}
          >
            <Archive sx={{ mr: 2, fontSize: 20, color: "#ef4444" }} /> Archive
          </MenuItem>
        )}
      </Menu>

      <Spacer size={3} direction="col" />

      {/* Archive Confirmation Modal */}
      <Modal
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        aria-labelledby="archive-document-modal"
        aria-describedby="modal-for-archiving-documents"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: 24,
            p: 4,
            outline: "none",
            textAlign: "center",
          }}
        >
          <h3
            style={{
              margin: "0 0 20px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Archive Document
          </h3>
          <p style={{ marginBottom: "24px", color: "#374151" }} className="text-wrap">
            Are you sure you want to archive this document? This action cannot be undone.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
            <button
              onClick={() => setArchiveModalOpen(false)}
              style={{
                padding: "10px 24px",
                backgroundColor: "#e5e7eb",
                color: "#374151",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
              disabled={archiveLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleArchiveConfirm}
              style={{
                padding: "10px 24px",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
              disabled={archiveLoading}
            >
              {archiveLoading ? "Archiving..." : "Archive"}
            </button>
          </div>
        </Box>
      </Modal>

      {/* Modal for Upload Document */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="upload-document-modal"
        aria-describedby="modal-for-uploading-documents"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: 24,
            p: 4,
            outline: "none",
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setModalOpen(false)}
            style={{
              position: "absolute",
              top: 12,
              right: 16,
              background: "none",
              border: "none",
              fontSize: "30px",
              color: "#374151",
              cursor: "pointer",
              zIndex: 2,
            }}
            aria-label="Close"
          >
            ×
          </button>
          <h3
            style={{
              margin: "0 0 20px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
              textAlign: "center",
            }}
          >
            Upload Document
          </h3>
          {/* Document Type Selection */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Document Type
            </label>
            <Box sx={{ width: "100%" }}>
              <SelectInput
                options={SELECT_FILETYPE}
                onHandleChange={onChangeFileTypeSelectInput}
                value={documentType}
                required={true}
                backgroundColor="inherit"
                placeholder="Select document type"
              />
            </Box>
          </div>
          {/* Show insurance fields if type is insurance */}
          {documentType === "INSURANCE" && (
            <>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Date of Loss
                </label>
                <input
                  type="date"
                  value={dateOfLoss}
                  onChange={(e) => setDateOfLoss(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Date of Scope
                </label>
                <input
                  type="date"
                  value={dateOfScope}
                  onChange={(e) => setDateOfScope(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </>
          )}
          {/* File Name Input */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              File Name (Optional)
            </label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Enter custom file name"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.outline = "none";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
              onMouseEnter={(e) => {
                const target = e.target as HTMLInputElement;
                if (document.activeElement !== target) {
                  target.style.borderColor = "#9ca3af";
                }
              }}
              onMouseLeave={(e) => {
                const target = e.target as HTMLInputElement;
                if (document.activeElement !== target) {
                  target.style.borderColor = "#d1d5db";
                }
              }}
            />
          </div>
          {/* File Upload Section */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Select File
            </label>
            <div
              style={{
                border: fileUrl.length > 0 ? "2px solid #10b981" : "2px dashed #d1d5db",
                borderRadius: "12px",
                padding: "20px",
                textAlign: "center",
                backgroundColor: fileUrl.length > 0 ? "#ecfdf5" : "#f9fafb",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onClick={() => {
                if (fileRef.current) {
                  fileRef.current.click();
                }
              }}
              onMouseEnter={(e) => {
                if (fileUrl.length === 0) {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }
              }}
              onMouseLeave={(e) => {
                if (fileUrl.length === 0) {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }
              }}
            >
              <input
                type="file"
                id="file"
                ref={fileRef}
                onChange={() => {
                  if (uploadUrl === "") {
                    getUplaodUrl();
                  }
                }}
                accept={"image/png, image/jpeg, image/jpg, application/pdf"}
                style={{
                  display: "none",
                }}
              />
              {fileUrl.length > 0 ? (
                <div>
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#10b981",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px auto",
                    }}
                  >
                    <FileOpen
                      style={{
                        width: 24,
                        height: 24,
                        color: "white",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#059669",
                    }}
                  >
                    File Selected
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    Click to change file
                  </p>
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px auto",
                    }}
                  >
                    <FileOpen
                      style={{
                        width: 24,
                        height: 24,
                        color: "#6b7280",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    Choose a file
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    PNG, JPG, PDF up to 10MB
                  </p>
                </div>
              )}
            </div>
          </div>
          {/* Upload Button */}
          <button
            onClick={async () => {
              await onUpload();
              setModalOpen(false);
            }}
            disabled={fileUrl.length < 1 || !documentType}
            style={{
              width: "100%",
              padding: "12px 24px",
              backgroundColor:
                fileUrl.length > 0 && documentType ? "#3b82f6" : "#e5e7eb",
              color: fileUrl.length > 0 && documentType ? "white" : "#9ca3af",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              cursor:
                fileUrl.length > 0 && documentType ? "pointer" : "not-allowed",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (fileUrl.length > 0 && documentType) {
                e.currentTarget.style.backgroundColor = "#2563eb";
              }
            }}
            onMouseLeave={(e) => {
              if (fileUrl.length > 0 && documentType) {
                e.currentTarget.style.backgroundColor = "#3b82f6";
              }
            }}
          >
            Upload Document
          </button>
        </Box>
      </Modal>

      {/* Edit Insurance Document Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        aria-labelledby="edit-insurance-document-modal"
        aria-describedby="modal-for-editing-insurance-document"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: 24,
            p: 4,
            outline: "none",
          }}
        >
          <button
            onClick={() => setEditModalOpen(false)}
            style={{
              position: "absolute",
              top: 12,
              right: 16,
              background: "none",
              border: "none",
              fontSize: "30px",
              color: "#374151",
              cursor: "pointer",
              zIndex: 2,
            }}
            aria-label="Close"
          >
            ×
          </button>
          <h3
            style={{
              margin: "0 0 20px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
              textAlign: "center",
            }}
          >
            Edit Insurance Document
          </h3>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              File Name
            </label>
            <input
              type="text"
              value={editFileName}
              onChange={(e) => setEditFileName(e.target.value)}
              placeholder="Enter file name"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Date of Loss
            </label>
            <input
              type="date"
              value={editDateOfLoss}
              onChange={(e) => setEditDateOfLoss(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Date of Scope
            </label>
            <input
              type="date"
              value={editDateOfScope}
              onChange={(e) => setEditDateOfScope(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                boxSizing: "border-box",
              }}
            />
          </div>
          <button
            onClick={handleEditSave}
            style={{
              width: "100%",
              padding: "12px 24px",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            Save Changes
          </button>
        </Box>
      </Modal>
    </>
  );
};